/**
 * /sudo — super-admin home.
 *
 * Platform-wide KPI cards over the top + a sortable-ish table of every
 * business below. Each row links to /sudo/businesses/<slug> for the
 * per-business drill-in.
 */
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useDocumentTitle } from '../lib/use-document-title';

export const Route = createFileRoute('/sudo/')({
  component: SudoDashboard,
});

interface Overview {
  generatedAt: number;
  totals: { businesses: number; users: number; bookingsAllTime: number };
  last7Days: { signups: number; bookings: number };
  last30Days: { bookings: number; activeBusinesses: number };
  risk: { dormantBusinesses: number };
}

interface BusinessRow {
  id: string;
  name: string;
  slug: string;
  planSlug: string;
  timezone: string;
  createdAt: number;
  ownerEmail: string | null;
  ownerName: string | null;
  bookings7d: number;
  bookings30d: number;
  bookingsAllTime: number;
  lastBookingAt: number | null;
}

async function fetchOverview(): Promise<Overview> {
  const res = await fetch('/api/super/overview');
  if (!res.ok) throw new Error('overview_failed');
  return res.json();
}

async function fetchBusinesses(): Promise<{ businesses: BusinessRow[] }> {
  const res = await fetch('/api/super/businesses');
  if (!res.ok) throw new Error('businesses_failed');
  return res.json();
}

function SudoDashboard() {
  useDocumentTitle('Super-admin');
  const overview = useQuery({ queryKey: ['sudo', 'overview'], queryFn: fetchOverview });
  const businesses = useQuery({
    queryKey: ['sudo', 'businesses'],
    queryFn: fetchBusinesses,
  });

  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Platform overview</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every business on Bookaburra. Read-only.
        </p>
      </header>

      {/* KPI cards */}
      <section>
        {overview.isLoading ? (
          <KpiSkeleton />
        ) : overview.data ? (
          <KpiGrid data={overview.data} />
        ) : (
          <p className="text-sm text-destructive">Failed to load overview.</p>
        )}
      </section>

      {/* Businesses */}
      <section>
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-semibold tracking-tight">Businesses</h2>
          {businesses.data ? (
            <span className="text-xs text-muted-foreground">
              {businesses.data.businesses.length} total
            </span>
          ) : null}
        </div>

        {businesses.isLoading ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading&hellip;</p>
        ) : businesses.data ? (
          <BusinessesTable rows={businesses.data.businesses} />
        ) : (
          <p className="mt-6 text-sm text-destructive">Failed to load businesses.</p>
        )}
      </section>
    </div>
  );
}

function KpiGrid({ data }: { data: Overview }) {
  const cards: { label: string; value: string; sub?: string }[] = [
    {
      label: 'Businesses',
      value: String(data.totals.businesses),
      sub: `${data.last30Days.activeBusinesses} active in last 30d`,
    },
    {
      label: 'Users',
      value: String(data.totals.users),
      sub: `${data.last7Days.signups} new this week`,
    },
    {
      label: 'Bookings · 7d',
      value: String(data.last7Days.bookings),
      sub: `${data.last30Days.bookings} in last 30d`,
    },
    {
      label: 'Bookings · all-time',
      value: String(data.totals.bookingsAllTime),
    },
    {
      label: 'Dormant businesses',
      value: String(data.risk.dormantBusinesses),
      sub: 'no bookings in 30d',
    },
  ];

  return (
    <ul className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {cards.map((c) => (
        <li
          key={c.label}
          className="rounded-2xl border border-brand-sage/15 bg-card p-5 shadow-brand"
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {c.label}
          </p>
          <p className="mt-2 text-3xl font-bold tracking-tight">{c.value}</p>
          {c.sub ? (
            <p className="mt-1 text-xs text-muted-foreground">{c.sub}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function KpiSkeleton() {
  return (
    <ul className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="h-[112px] animate-pulse rounded-2xl border border-brand-sage/15 bg-card shadow-brand"
        />
      ))}
    </ul>
  );
}

function BusinessesTable({ rows }: { rows: BusinessRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="mt-6 text-sm text-muted-foreground">No businesses yet.</p>
    );
  }
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-brand-sage/15 bg-card shadow-brand">
      <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-sm">
        <thead className="border-b border-brand-sage/10 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <tr>
            <th className="px-4 py-3">Business</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Plan</th>
            <th className="px-4 py-3 text-right">7d</th>
            <th className="px-4 py-3 text-right">30d</th>
            <th className="px-4 py-3 text-right">All-time</th>
            <th className="px-4 py-3">Last booking</th>
            <th className="px-4 py-3" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-brand-sage/10 last:border-b-0 hover:bg-secondary/40"
            >
              <td className="px-4 py-3">
                <Link
                  to="/sudo/businesses/$slug"
                  params={{ slug: r.slug }}
                  className="font-semibold underline-offset-4 hover:underline"
                >
                  {r.name}
                </Link>
                <p className="text-xs text-muted-foreground">/{r.slug}</p>
              </td>
              <td className="px-4 py-3">
                {r.ownerEmail ? (
                  <span className="text-xs">
                    {r.ownerName ? (
                      <>
                        <span className="block">{r.ownerName}</span>
                        <span className="block text-muted-foreground">
                          {r.ownerEmail}
                        </span>
                      </>
                    ) : (
                      r.ownerEmail
                    )}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <span className="inline-flex rounded-md border border-border bg-secondary/40 px-2 py-0.5 text-xs font-medium uppercase tracking-wider">
                  {r.planSlug}
                </span>
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{r.bookings7d}</td>
              <td className="px-4 py-3 text-right tabular-nums">{r.bookings30d}</td>
              <td className="px-4 py-3 text-right tabular-nums">
                {r.bookingsAllTime}
              </td>
              <td className="px-4 py-3 text-xs text-muted-foreground">
                {r.lastBookingAt ? formatRelative(r.lastBookingAt) : 'never'}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <a
                    href={`/book/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary"
                    title="Open public booking page"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <Link
                    to="/sudo/businesses/$slug"
                    params={{ slug: r.slug }}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-secondary"
                  >
                    Details
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 0) return 'in the future';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
